import { EChartsOption } from 'echarts';
import { getChartThemeColors } from '../../../../utils/chartTheme';

export const getVerticalBarChartSize = {
  height: '235px',
  width: '100%',
};

export const prepareAndGetSentimentStackedBarOptions = (
  formatter: any,
  data: (string | number)[][],
  seriesColors: { name: string; color: string }[]
): EChartsOption => {
  const headers = data[0];
  const seriesNames = headers.slice(1) as string[];
  const theme = getChartThemeColors();

  return {
    tooltip: {
      borderRadius: 8,
      padding: [5, 10],
      borderColor: theme.tooltipBorder,
      backgroundColor: theme.tooltipBg,
      textStyle: {
        color: theme.tooltipText,
        fontFamily: 'Inter,sans-serif',
        fontSize: 12,
        lineHeight: 28,
        fontWeight: 600,
      },
      trigger: 'item',
      formatter,
    },
    legend: { show: false },
    grid: { top: 0, bottom: 0, right: 0, left: 0, containLabel: true },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        fontSize: '11px',
        fontWeight: 'normal',
        fontFamily: 'Inter,sans-serif',
        show: true,
      },
    },
    dataset: { source: data },
    series: seriesNames.map((name) => ({
      type: 'bar' as const,
      name,
      stack: 'sentiment',
      barCategoryGap: '50%',
      itemStyle: {
        color: seriesColors.find((s) => s.name === name)?.color || '#ccc',
        borderRadius:
          name === seriesNames[0]
            ? [2, 0, 0, 2]
            : name === seriesNames[seriesNames.length - 1]
            ? [0, 2, 2, 0]
            : [0, 0, 0, 0],
      },
      encode: { x: name, y: String(headers[0]) },
    })),
  };
};

export const prepareAndGetHorizontalBarOptions = (
  formatter: any,
  data: (string | null)[][],
  colorMap: Map<string, string>
): EChartsOption => {
  const theme = getChartThemeColors();
  return {
    tooltip: {
      borderRadius: 8,
      padding: [5, 10],
      borderColor: theme.tooltipBorder,
      backgroundColor: theme.tooltipBg,
      textStyle: {
        color: theme.tooltipText,
        fontFamily: 'Inter,sans-serif',
        fontSize: 12,
        lineHeight: 28,
        fontWeight: 600,
      },
      trigger: 'item',
      formatter,
    },
    legend: {
      show: false,
    },
    grid: {
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      containLabel: true,
    },
    xAxis: {
      min: 0,
      max: 100,
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        show: false,
      },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        fontSize: '10px',
        fontWeight: 'normal',
        fontFamily: 'Inter,sans-serif',
        show: true,
      },
    },
    dataset: {
      source: data || [],
    },
    series: [
      {
        type: 'bar',
        barCategoryGap: '60%',
        itemStyle: {
          borderRadius: [0, 2, 2, 0],
          color: (params) => colorMap.get(params.name) || '',
        },
      },
    ],
  };
};

export const prepareAndGetVerticalBarOptions = (
  formatter: any,
  data: (string | null)[][],
  colorMap: Map<string, string>,
  max = 100,
  /**
   * Wypisuje wartość nad słupkiem. Raport tego potrzebuje, bo jego PDF to zrzut TEGO wykresu —
   * bez etykiet czytelnik dokumentu nie ma skąd wziąć liczb (tooltipy działają tylko na ekranie).
   */
  showValueLabels = false
): EChartsOption => {
  const theme = getChartThemeColors();
  return {
    tooltip: {
      borderRadius: 8,
      padding: [5, 10],
      borderColor: theme.tooltipBorder,
      backgroundColor: theme.tooltipBg,
      textStyle: {
        color: theme.tooltipText,
        fontFamily: 'Inter,sans-serif',
        fontSize: 12,
        lineHeight: 28,
        fontWeight: 600,
      },
      trigger: 'item',
      formatter,
    },
    legend: {
      show: false,
    },
    grid: {
      top: '50px',
      bottom: 1,
      right: 0,
      left: 0,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        fontFamily: 'Inter,sans-serif',
        fontSize: '12px',
        fontWeight: 'normal',
        overflow: 'truncate',
        width: 120,
        margin: 26,
        interval: 0,
      },
    },
    yAxis: {
      min: 0,
      max,
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: theme.splitLine } },
      axisLabel: {
        show: false,
      },
    },
    dataset: {
      source: data || [],
    },
    series: [
      {
        type: 'bar',
        barCategoryGap: '60%',
        label: {
          show: showValueLabels,
          position: 'top',
          formatter: '{@[1]}%',
          fontFamily: 'Inter,sans-serif',
          fontSize: 11,
          fontWeight: 600,
          color: theme.tooltipText,
        },
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
          color: (params) => colorMap.get(params.name) || '',
        },
      },
    ],
  };
};
